import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface StickyHeaderProps {
  onOpenConsultation: () => void;
}

const SERVICE_LINKS = [
  { label: 'Personal Care', to: '/services/personal-care' },
  { label: 'Companion Care', to: '/services/companion-care' },
  { label: "Alzheimer's & Dementia Care", to: '/services/alzheimers-dementia-care' },
  { label: 'Respite Care', to: '/services/respite-care' },
  { label: 'Post-Hospital Recovery', to: '/services/post-hospital-recovery' },
];

export default function StickyHeader({ onOpenConsultation }: StickyHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  const textColor = 'text-navy hover:text-gold';

  return (
    <header
      className="fixed top-0 left-0 w-full z-50 bg-white border-b border-gray-100"
      style={{ boxShadow: '0 2px 24px rgba(15,42,68,0.07)' }}
    >
      <div className="max-w-7xl mx-auto px-6 h-[76px] flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img
            src="https://lirp.cdn-website.com/035eb9cd/dms3rep/multi/opt/The+Oaks+Manor-1920w.png"
            alt="Oak Senior Care - The Oaks Manor"
            className="h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          <Link to="/" className={`text-sm font-semibold tracking-wide transition-colors ${textColor}`}>
            Home
          </Link>

          {/* Services dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button className={`flex items-center gap-1.5 text-sm font-semibold tracking-wide transition-colors cursor-pointer ${textColor}`}>
              Services
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {servicesOpen && (
              <div
                className="absolute top-full left-0 mt-3 w-68 bg-white rounded-2xl py-2.5 border border-gray-100 z-50"
                style={{ boxShadow: '0 12px 40px rgba(15,42,68,0.14)', width: '270px' }}
              >
                <div className="px-4 py-2 border-b border-gray-50 mb-1">
                  <p className="text-gold text-[10px] font-bold uppercase tracking-widest">Our Services</p>
                </div>
                {SERVICE_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-3 px-4 py-3 text-navy text-sm hover:bg-offWhite hover:text-gold transition-colors"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-gold text-base"></i>
                    </span>
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/about" className={`text-sm font-semibold tracking-wide transition-colors ${textColor}`}>
            About
          </Link>
          <Link to="/locations/oakland-il" className={`text-sm font-semibold tracking-wide transition-colors ${textColor}`}>
            Locations
          </Link>
        </nav>

        {/* Right CTAs */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:2173462042"
            className="flex items-center gap-2.5 text-sm font-bold text-navy hover:text-gold transition-colors"
          >
            <span className="w-9 h-9 flex items-center justify-center bg-gold/15 rounded-full hover:bg-gold/25 transition-colors">
              <i className="ri-phone-fill text-gold text-base"></i>
            </span>
            (217) 346-2042
          </a>
          <button
            onClick={onOpenConsultation}
            className="whitespace-nowrap bg-gold text-navy text-sm font-bold px-7 py-3 rounded-full hover:brightness-110 transition-all cursor-pointer"
          >
            Free Consultation
          </button>
        </div>

        {/* Mobile controls */}
        <div className="flex lg:hidden items-center gap-3">
          <a
            href="tel:2173462042"
            className="inline-flex items-center gap-1.5 bg-gold text-navy text-xs font-bold px-4 py-2.5 rounded-full whitespace-nowrap"
          >
            <i className="ri-phone-fill text-sm"></i>
            Call Now
          </a>
          <button
            className="w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg text-navy hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-6 py-6" style={{ boxShadow: '0 8px 30px rgba(15,42,68,0.10)' }}>
          <div className="flex flex-col gap-0">
            <Link to="/" className="text-navy font-semibold text-base py-3.5 border-b border-gray-50 flex items-center justify-between">
              Home <i className="ri-arrow-right-s-line text-gray-300"></i>
            </Link>
            <div className="py-3.5 border-b border-gray-50">
              <p className="text-navy font-semibold text-base mb-3">Services</p>
              <div className="flex flex-col gap-0.5 pl-2">
                {SERVICE_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-2 text-navy/65 text-sm py-2 hover:text-gold transition-colors"
                  >
                    <i className="ri-arrow-right-s-line text-gold"></i>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <Link to="/about" className="text-navy font-semibold text-base py-3.5 border-b border-gray-50 flex items-center justify-between">
              About <i className="ri-arrow-right-s-line text-gray-300"></i>
            </Link>
            <Link to="/locations/oakland-il" className="text-navy font-semibold text-base py-3.5 border-b border-gray-50 flex items-center justify-between">
              Locations <i className="ri-arrow-right-s-line text-gray-300"></i>
            </Link>
          </div>
          <button
            onClick={() => { onOpenConsultation(); setMobileOpen(false); }}
            className="whitespace-nowrap w-full bg-navy text-white font-bold py-4 rounded-full mt-5 cursor-pointer text-sm"
          >
            Schedule Free Consultation
          </button>
          <div className="mt-4 text-center">
            <p className="text-gray-400 text-xs">Mon – Sun, 8am – 6pm · (217) 346-2042</p>
          </div>
        </div>
      )}
    </header>
  );
}
